if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        this.stackPayloadError = null;
        // One-time purchases keep the native bundle submission (bundle product +
        // Avada bundle quantity/discount inputs). Only subscription stacks are
        // split into component line items.
        const stackForm = this.dataset.stackForm === 'true';
        const stackSubscribe = stackForm && this.isStackSubscriptionSelected();
        const stackPayload = stackSubscribe ? this.buildStackPayload(formData) : null;
        if (stackSubscribe && !stackPayload) {
          this.handleErrorMessage(this.stackPayloadError || 'This stack is missing component products.');
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
          return;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }
        if (stackPayload) {
          config.body = JSON.stringify(stackPayload);
        } else {
          delete config.headers['Content-Type'];
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: stackPayload ? stackPayload.items.map((item) => item.id).join(',') : formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: stackPayload ? stackPayload.items.map((item) => item.id).join(',') : formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      buildStackPayload(formData) {
        if (this.dataset.stackForm !== 'true') return null;

        // Follow the flavors picked on the bundle picker: the variant map
        // (rendered by stack-component-inputs) maps the currently selected
        // bundle variant to each component variant, overriding the server-side
        // defaults (children's first variants).
        this.syncStackComponentVariants();

        const components = Array.from(this.form.querySelectorAll('[data-stack-component-variant-id]'))
          .map((input, index) => {
            const id = parseInt(input.value, 10);
            if (!id) return null;

            return {
              id,
              index,
              title: input.dataset.stackComponentTitle || '',
              handle: input.dataset.stackComponentHandle || '',
              image: input.dataset.stackComponentImage || '',
              quantity: parseInt(input.dataset.stackComponentQuantity || '1', 10) || 1,
              available: input.dataset.stackComponentAvailable !== 'false',
            };
          })
          .filter(Boolean);

        if (!components.length) return null;

        const unavailable = components.find((component) => !component.available);
        if (unavailable) {
          this.stackPayloadError = `${unavailable.title || 'A stack item'} is currently unavailable.`;
          return null;
        }

        const sellingPlanId = parseInt(formData.get('selling_plan'), 10);
        if (this.isStackSubscriptionSelected() && !sellingPlanId) {
          this.stackPayloadError = 'Please choose a subscription plan for this stack.';
          return null;
        }

        const stackQuantity = parseInt(formData.get('quantity') || '1', 10) || 1;
        const stackHandle = this.dataset.stackHandle || formData.get('id') || 'stack';
        const stackVariantId = formData.get('id') || stackHandle;
        const stackSellingPlanKey = sellingPlanId ? String(sellingPlanId) : 'no-plan';
        const stackTitle = this.dataset.stackTitle || 'Stack';
        // Stable per stack variant + selling plan: identical flavor/frequency
        // adds merge, but different variants or frequencies stay separate.
        const groupKey = `${stackHandle}:${stackVariantId}:${stackSellingPlanKey}`;
        const items = components.map((component) => {
          const item = {
            id: component.id,
            quantity: component.quantity * stackQuantity,
            properties: {
              _stack_id: stackHandle,
              _stack_title: stackTitle,
              Bundle: stackTitle,
              _stack_group_key: groupKey,
              _stack_component_index: String(component.index + 1),
              _stack_variant_id: String(stackVariantId),
              _stack_selling_plan_id: stackSellingPlanKey,
            },
          };

          if (component.handle) item.properties._stack_component_handle = component.handle;
          if (component.title) item.properties._stack_component_title = component.title;
          if (component.image) item.properties._stack_component_image = component.image;
          if (this.dataset.stackImage) item.properties._stack_image = this.dataset.stackImage;
          if (sellingPlanId) item.selling_plan = sellingPlanId;

          return item;
        });

        const payload = {
          items,
        };

        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        return payload;
      }

      isStackSubscriptionSelected() {
        if (!this.form) return false;
        // Use getAttribute('id') — the variant <input name="id"> shadows the
        // form's `id` property via named access, which would break matching.
        const formId = this.form.getAttribute('id');
        if (!formId) return false;
        const customFrequency = Array.from(document.querySelectorAll('custom-frequency')).find(
          (element) => element.dataset.productFormId === formId
        );
        return !!customFrequency?.querySelector('[data-cf-row="subscribe"].is-active');
      }

      syncStackComponentVariants() {
        const id = this.variantIdInput && this.variantIdInput.value;
        const mapEl = this.form.querySelector('[data-stack-variant-map]');
        if (!id || !mapEl) return;

        let entry;
        try {
          entry = JSON.parse(mapEl.textContent)[id];
        } catch (e) {
          return;
        }
        if (!entry) return;

        const inputs = Array.from(this.form.querySelectorAll('[data-stack-component-variant-id]'));
        Object.keys(entry).forEach((index) => {
          const input = inputs[Number(index)];
          if (!input) return;
          if (entry[index].id) input.value = String(entry[index].id);
          if (entry[index].available !== undefined) {
            input.dataset.stackComponentAvailable = String(entry[index].available);
          }
        });
      }
    }
  );
}
